import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Activity,
  ArrowUpRight,
  TrendingUp,
  Users,
  Bed,
  Heart,
  ClipboardCheck,
  BrainCircuit,
  CalendarClock,
  ShieldCheck,
  Bot,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  FileSearch,
  CircleCheck,
  CircleAlert,
  Loader,
  Send,
  Stethoscope,
  ScanLine,
  FileSignature,
  FolderSearch,
  FileText,
  User,
  Pen,
  Fingerprint,
  Search,
  ScanSearch,
} from "lucide-react";
import TechBackground from "@/components/fx/TechBackground";
import ScrambleText from "@/components/fx/ScrambleText";

/* ============ shared window frame ============ */
function Frame({ title, subtitle, live = true, children }) {
  return (
    <div className="relative bg-[#0B1220] border border-gray-800 shadow-2xl overflow-hidden">
      {/* Chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 bg-[#0B1220]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
        <span className="w-2.5 h-2.5 rounded-full bg-gray-600" />
        <span className="ml-3 font-mono-axis text-[10px] uppercase tracking-widest text-gray-500 truncate">
          {title} <span className="text-gray-700">·</span>{" "}
          <span className="text-gray-400">{subtitle}</span>
        </span>
        {live && (
          <span className="ml-auto flex items-center gap-2 font-mono-axis text-[10px] text-[#3B82F6] shrink-0">
            <span className="relative w-1.5 h-1.5 rounded-full bg-[#3B82F6] text-[#3B82F6] live-dot" />
            live
          </span>
        )}
      </div>
      <div className="p-5 lg:p-6">{children}</div>
    </div>
  );
}

/* ============ Counter ============ */
function Counter({ to, decimals = 0, suffix = "", duration = 1.2, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) {
      setN(0);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, to, duration]);

  return (
    <span>
      {n.toFixed(decimals)}
      {suffix}
    </span>
  );
}

/* ============ Bars ============ */
const WELY_BARS = [
  { h: 32, c: "#3B82F6" },
  { h: 48, c: "#3B82F6" },
  { h: 40, c: "#3B82F6" },
  { h: 66, c: "#06B6D4" },
  { h: 55, c: "#3B82F6" },
  { h: 78, c: "#1E3A8A" },
  { h: 62, c: "#06B6D4" },
  { h: 88, c: "#1E3A8A" },
  { h: 72, c: "#3B82F6" },
  { h: 94, c: "#1E3A8A" },
  { h: 80, c: "#06B6D4" },
  { h: 96, c: "#1E3A8A" },
];

/* ============ 1. WELY ============ */
function WelyMockup({ active }) {
  return (
    <Frame title="Wely" subtitle="BI Hospitalar">
      <div className="space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: Bed, label: "Ocupação", value: 87.4, suf: "%", d: 1, c: "#3B82F6" },
            { icon: Users, label: "Atendimentos", value: 1284, suf: "", d: 0, c: "#06B6D4" },
            { icon: Heart, label: "TMP (dias)", value: 4.2, suf: "", d: 1, c: "#3B82F6" },
            { icon: TrendingUp, label: "Receita", value: 12.6, suf: "M", d: 1, c: "#1E3A8A" },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.label}
                className="bg-[#111827] border border-gray-800 p-3"
              >
                <Icon className="w-3.5 h-3.5 mb-2" style={{ color: k.c }} strokeWidth={1.75} />
                <p className="font-display text-lg lg:text-xl font-semibold text-white tabular-nums">
                  <Counter to={k.value} decimals={k.d} suffix={k.suf} active={active} />
                </p>
                <p className="mt-1 font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
                  {k.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <div className="bg-[#111827] border border-gray-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
                Faturamento mensal
              </p>
              <p className="mt-1 font-display text-sm font-medium text-white">
                Últimos 12 meses · <span className="text-[#3B82F6]">+14.8%</span>
              </p>
            </div>
            <div className="flex items-center gap-2 font-mono-axis text-[10px] text-gray-500">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-[#3B82F6]" /> Convênios
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 bg-[#1E3A8A]" /> Particular
              </span>
            </div>
          </div>
          <div className="relative h-24 lg:h-28 flex items-end gap-1.5">
            {WELY_BARS.map((b, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={active ? { height: `${b.h}%` } : { height: 0 }}
                transition={{
                  duration: 0.7,
                  delay: 0.05 * i,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="flex-1 origin-bottom"
                style={{ backgroundColor: b.c, minHeight: 2 }}
              />
            ))}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="sweep absolute inset-y-0 w-1/6 bg-gradient-to-r from-transparent via-[#3B82F6]/10 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 2. HUGGIN ============ */
function HugginMockup({ active }) {
  const rows = [
    { id: "AT-8921", status: "conferido", conv: "Unimed", v: "R$ 8.420", flag: "ok" },
    { id: "AT-8918", status: "inconsist.", conv: "Bradesco", v: "R$ 12.780", flag: "warn" },
    { id: "AT-8917", status: "auditoria", conv: "Amil", v: "R$ 4.120", flag: "loading" },
    { id: "AT-8914", status: "conferido", conv: "SulAmérica", v: "R$ 6.890", flag: "ok" },
    { id: "AT-8912", status: "inconsist.", conv: "Unimed", v: "R$ 15.640", flag: "warn" },
  ];
  return (
    <Frame title="HUGGIN" subtitle="Auditoria hospitalar">
      <div className="grid grid-cols-3 gap-4">
        {/* Left: summary */}
        <div className="col-span-1 space-y-3">
          {[
            { label: "Contas auditadas", value: 4287, c: "#3B82F6", icon: ClipboardCheck },
            { label: "Inconsistências", value: 128, c: "#DC2626", icon: CircleAlert },
            { label: "Redução tempo", value: 62, suf: "%", c: "#06B6D4", icon: TrendingUp },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-[#111827] border border-gray-800 p-3">
                <Icon className="w-3.5 h-3.5 mb-2" style={{ color: k.c }} strokeWidth={1.75} />
                <p className="font-display text-lg font-semibold text-white tabular-nums">
                  <Counter to={k.value} suffix={k.suf || ""} decimals={0} active={active} />
                </p>
                <p className="mt-1 font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
                  {k.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right: table */}
        <div className="col-span-2 bg-[#111827] border border-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Atendimentos em auditoria
            </p>
            <span className="font-mono-axis text-[10px] text-[#3B82F6]">
              conferência em tempo real
            </span>
          </div>
          <div>
            {rows.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, x: -20 }}
                animate={active ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ delay: 0.08 * i, duration: 0.4 }}
                className="grid grid-cols-12 items-center gap-2 px-4 py-2.5 border-b border-gray-800/60 hover:bg-white/5 transition-colors last:border-b-0"
              >
                <span className="col-span-3 font-mono-axis text-[11px] text-gray-400">
                  {r.id}
                </span>
                <span className="col-span-3 text-xs text-white">{r.conv}</span>
                <span className="col-span-3 flex items-center gap-1.5">
                  {r.flag === "ok" && <CircleCheck className="w-3 h-3 text-[#3B82F6]" />}
                  {r.flag === "warn" && <CircleAlert className="w-3 h-3 text-[#DC2626]" />}
                  {r.flag === "loading" && (
                    <Loader className="w-3 h-3 text-[#06B6D4] animate-spin" />
                  )}
                  <span className="text-[11px] text-gray-400 capitalize">{r.status}</span>
                </span>
                <span className="col-span-3 text-right text-xs text-white font-medium tabular-nums">
                  {r.v}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 3. MUNINN ============ */
function MuninnMockup({ active }) {
  const motives = [
    { name: "Divergência de código", pct: 42, c: "#DC2626" },
    { name: "Falta de autorização", pct: 28, c: "#1E3A8A" },
    { name: "Documentação incompleta", pct: 18, c: "#3B82F6" },
    { name: "Prazo expirado", pct: 12, c: "#06B6D4" },
  ];
  return (
    <Frame title="MUNINN" subtitle="IA para análise de glosas">
      <div className="grid grid-cols-5 gap-4">
        {/* Left: file processing */}
        <div className="col-span-2 bg-[#111827] border border-gray-800 p-4">
          <div className="flex items-center gap-2 mb-3">
            <BrainCircuit className="w-4 h-4 text-[#3B82F6]" strokeWidth={1.75} />
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Processamento IA
            </p>
          </div>
          <div className="space-y-2">
            {[
              { name: "guias_out_2026.xml", size: "12.4 MB", state: "done" },
              { name: "guias_set_2026.xml", size: "9.8 MB", state: "done" },
              { name: "guias_ago_2026.xml", size: "11.2 MB", state: "processing" },
              { name: "guias_jul_2026.xml", size: "10.6 MB", state: "queued" },
            ].map((f, i) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 8 }}
                animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                transition={{ delay: 0.1 * i, duration: 0.4 }}
                className="border border-gray-800 p-2.5"
              >
                <div className="flex items-center gap-2">
                  <FileSearch className="w-3 h-3 text-gray-500 shrink-0" />
                  <p className="text-[11px] text-white truncate flex-1 font-mono-axis">
                    {f.name}
                  </p>
                  {f.state === "done" && (
                    <CircleCheck className="w-3 h-3 text-[#3B82F6]" />
                  )}
                  {f.state === "processing" && (
                    <Loader className="w-3 h-3 text-[#06B6D4] animate-spin" />
                  )}
                  {f.state === "queued" && (
                    <span className="w-2 h-2 bg-gray-600 rounded-full" />
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">{f.size}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-800">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500">
              Registros processados
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#3B82F6] tabular-nums">
              <Counter to={12842} active={active} />
            </p>
          </div>
        </div>

        {/* Right: motives chart */}
        <div className="col-span-3 bg-[#111827] border border-gray-800 p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Motivos de glosa · classificação IA
            </p>
            <p className="font-mono-axis text-[10px] text-[#3B82F6]">
              R$ <Counter to={487} suffix="k" active={active} /> recuperáveis
            </p>
          </div>
          <div className="space-y-3">
            {motives.map((m, i) => (
              <div key={m.name}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] text-white">{m.name}</span>
                  <span className="font-mono-axis text-[11px] text-gray-400 tabular-nums">
                    {m.pct}%
                  </span>
                </div>
                <div className="h-1.5 bg-[#0B1220] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={active ? { width: `${m.pct}%` } : { width: 0 }}
                    transition={{
                      duration: 0.9,
                      delay: 0.15 + 0.1 * i,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="h-full"
                    style={{ backgroundColor: m.c }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 4. EIRAI ============ */
function EiraiMockup({ active }) {
  const messages = [
    {
      from: "user",
      text: "Paciente 62a, dor torácica retroesternal + sudorese. ECG anexo.",
      delay: 0.1,
    },
    {
      from: "ai",
      text: "Análise EIRAI: supradesnivelamento ST em DII, DIII, aVF. Padrão compatível com IAM inferior. Sugestão: ativar protocolo dor torácica.",
      delay: 0.5,
    },
    {
      from: "user",
      text: "Diferencial a considerar?",
      delay: 1.2,
    },
    {
      from: "ai",
      text: "Considerar: pericardite (repolarização difusa), TEP (D-dímero, angio-TC), dissecção aórtica (assimetria PA, TC). Correlacionar troponina seriada.",
      delay: 1.6,
    },
  ];
  return (
    <Frame title="EIRAI" subtitle="Assistente clínico com IA">
      <div className="grid grid-cols-3 gap-4">
        {/* Left: chat */}
        <div className="col-span-2 bg-[#111827] border border-gray-800 flex flex-col h-[320px]">
          <div className="px-4 py-2.5 border-b border-gray-800 flex items-center gap-2">
            <div className="relative">
              <Bot className="w-4 h-4 text-[#3B82F6]" strokeWidth={1.75} />
              <span className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
            </div>
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Consulta clínica
            </p>
          </div>
          <div className="flex-1 overflow-hidden p-3 space-y-2">
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
                transition={{ delay: m.delay, duration: 0.4 }}
                className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 text-[11px] leading-relaxed ${
                    m.from === "user"
                      ? "bg-[#1E3A8A]/20 border border-[#1E3A8A]/40 text-white"
                      : "bg-[#0B1220] border border-[#3B82F6]/30 text-gray-200"
                  }`}
                >
                  {m.from === "ai" && (
                    <span className="font-mono-axis text-[9px] uppercase tracking-widest text-[#3B82F6] mb-1 block">
                      EIRAI · IA
                    </span>
                  )}
                  {m.text}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-gray-800 flex items-center gap-2">
            <input
              readOnly
              placeholder="Pergunte ao EIRAI..."
              className="flex-1 bg-transparent text-[11px] text-gray-500 outline-none placeholder:text-gray-600"
            />
            <Send className="w-3.5 h-3.5 text-[#3B82F6]" />
          </div>
        </div>

        {/* Right: exam scan panel */}
        <div className="col-span-1 bg-[#111827] border border-gray-800 p-4">
          <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400 mb-3">
            Leitura de exames
          </p>
          {/* ECG-like waveform */}
          <div className="relative h-24 bg-[#0B1220] border border-gray-800 overflow-hidden mb-3">
            <svg
              viewBox="0 0 200 80"
              className="w-full h-full"
              preserveAspectRatio="none"
            >
              <motion.path
                d="M 0 40 L 20 40 L 25 40 L 30 30 L 35 50 L 40 20 L 45 60 L 50 40 L 80 40 L 85 40 L 90 30 L 95 50 L 100 20 L 105 60 L 110 40 L 140 40 L 145 40 L 150 30 L 155 50 L 160 20 L 165 60 L 170 40 L 200 40"
                fill="none"
                stroke="#3B82F6"
                strokeWidth="1.5"
                initial={{ pathLength: 0 }}
                animate={active ? { pathLength: 1 } : { pathLength: 0 }}
                transition={{ duration: 2, ease: "linear" }}
              />
            </svg>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="sweep absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-[#3B82F6]/20 to-transparent" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ScanLine className="w-3 h-3 text-[#3B82F6]" />
              <span className="text-[10px] text-gray-300">ECG 12 derivações</span>
            </div>
            <div className="flex items-center gap-2">
              <Stethoscope className="w-3 h-3 text-[#3B82F6]" />
              <span className="text-[10px] text-gray-300">Padrão IAM inferior</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#3B82F6]" />
              <span className="text-[10px] text-gray-300">
                Confiança IA:{" "}
                <span className="text-[#3B82F6] font-medium tabular-nums">
                  <Counter to={94.7} decimals={1} suffix="%" active={active} />
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 5. EscalaMed ============ */
function EscalaMedMockup({ active }) {
  const DAYS = ["S", "T", "Q", "Q", "S", "S", "D"];
  const week1 = ["PS", "MM", "AN", "PS", "MM", "-", "-"];
  const week2 = ["MM", "AN", "PS", "MM", "-", "PS", "AN"];
  const week3 = ["AN", "PS", "MM", "AN", "PS", "MM", "-"];
  const week4 = ["PS", "MM", "AN", "-", "MM", "AN", "PS"];
  const weeks = [week1, week2, week3, week4];

  const colorFor = (v) => {
    if (v === "PS") return "#1E3A8A";
    if (v === "MM") return "#3B82F6";
    if (v === "AN") return "#06B6D4";
    return null;
  };
  return (
    <Frame title="EscalaMed" subtitle="Escalas médicas">
      <div className="grid grid-cols-3 gap-4">
        {/* Left: KPIs */}
        <div className="col-span-1 space-y-3">
          {[
            { label: "Cobertura", value: 96, suf: "%", c: "#3B82F6", icon: CalendarClock },
            { label: "Plantões / mês", value: 148, suf: "", c: "#06B6D4", icon: Users },
            { label: "Trocas automáticas", value: 24, suf: "", c: "#1E3A8A", icon: Sparkles },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-[#111827] border border-gray-800 p-3">
                <Icon className="w-3.5 h-3.5 mb-2" style={{ color: k.c }} strokeWidth={1.75} />
                <p className="font-display text-lg font-semibold text-white tabular-nums">
                  <Counter to={k.value} decimals={0} suffix={k.suf} active={active} />
                </p>
                <p className="mt-1 font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
                  {k.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right: calendar */}
        <div className="col-span-2 bg-[#111827] border border-gray-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Escala · Novembro 2026
            </p>
            <div className="flex gap-2 font-mono-axis text-[9px]">
              <span className="inline-flex items-center gap-1 text-gray-400">
                <span className="w-2 h-2 bg-[#1E3A8A]" /> PS
              </span>
              <span className="inline-flex items-center gap-1 text-gray-400">
                <span className="w-2 h-2 bg-[#3B82F6]" /> MM
              </span>
              <span className="inline-flex items-center gap-1 text-gray-400">
                <span className="w-2 h-2 bg-[#06B6D4]" /> AN
              </span>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS.map((d, i) => (
              <span
                key={i}
                className="text-center font-mono-axis text-[9px] text-gray-500 uppercase"
              >
                {d}
              </span>
            ))}
          </div>

          <div className="space-y-1">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((cell, ci) => {
                  const color = colorFor(cell);
                  return (
                    <motion.div
                      key={ci}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={active ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.85 }}
                      transition={{
                        delay: 0.02 * (wi * 7 + ci),
                        duration: 0.3,
                      }}
                      className="aspect-square flex items-center justify-center border border-gray-800 text-[10px] font-medium"
                      style={
                        color
                          ? { backgroundColor: color, color: "#fff", borderColor: color }
                          : { color: "#4B5563" }
                      }
                    >
                      {cell === "-" ? "" : cell}
                    </motion.div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 6. Autorizações ============ */
function AutorizacoesMockup({ active }) {
  const rows = [
    { portal: "Unimed", protocolo: "PR-2841", state: "captured", time: "2s" },
    { portal: "Bradesco Saúde", protocolo: "PR-2840", state: "captured", time: "3s" },
    { portal: "SulAmérica", protocolo: "PR-2839", state: "processing", time: "—" },
    { portal: "Amil", protocolo: "PR-2838", state: "captured", time: "2s" },
    { portal: "Hapvida", protocolo: "PR-2837", state: "queued", time: "—" },
  ];
  return (
    <Frame title="Autorizações" subtitle="Automação de portais">
      <div className="grid grid-cols-3 gap-4">
        {/* Left: stats */}
        <div className="col-span-1 space-y-3">
          {[
            { label: "Guias autorizadas", value: 1284, c: "#3B82F6", icon: ShieldCheck },
            { label: "Horas economizadas", value: 412, suf: "h", c: "#06B6D4", icon: TrendingUp },
            { label: "Portais integrados", value: 12, c: "#1E3A8A", icon: Activity },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-[#111827] border border-gray-800 p-3">
                <Icon className="w-3.5 h-3.5 mb-2" style={{ color: k.c }} strokeWidth={1.75} />
                <p className="font-display text-lg font-semibold text-white tabular-nums">
                  <Counter to={k.value} decimals={0} suffix={k.suf || ""} active={active} />
                </p>
                <p className="mt-1 font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
                  {k.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right: portals feed */}
        <div className="col-span-2 bg-[#111827] border border-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Robôs · portais em execução
            </p>
            <span className="flex items-center gap-2 font-mono-axis text-[10px] text-[#3B82F6]">
              <span className="relative w-1.5 h-1.5 rounded-full bg-[#3B82F6] text-[#3B82F6] live-dot" />
              6 robôs ativos
            </span>
          </div>
          <div>
            {rows.map((r, i) => (
              <motion.div
                key={r.protocolo}
                initial={{ opacity: 0, x: -20 }}
                animate={active ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ delay: 0.08 * i, duration: 0.4 }}
                className="grid grid-cols-12 items-center gap-2 px-4 py-2.5 border-b border-gray-800/60 hover:bg-white/5 transition-colors last:border-b-0"
              >
                <span className="col-span-4 text-xs text-white">{r.portal}</span>
                <span className="col-span-3 font-mono-axis text-[11px] text-gray-400">
                  {r.protocolo}
                </span>
                <span className="col-span-3 flex items-center gap-1.5">
                  {r.state === "captured" && (
                    <>
                      <CircleCheck className="w-3 h-3 text-[#3B82F6]" />
                      <span className="text-[11px] text-gray-400">capturado</span>
                    </>
                  )}
                  {r.state === "processing" && (
                    <>
                      <Loader className="w-3 h-3 text-[#06B6D4] animate-spin" />
                      <span className="text-[11px] text-gray-400">executando</span>
                    </>
                  )}
                  {r.state === "queued" && (
                    <>
                      <span className="w-2 h-2 bg-gray-600 rounded-full" />
                      <span className="text-[11px] text-gray-400">fila</span>
                    </>
                  )}
                </span>
                <span className="col-span-2 text-right font-mono-axis text-[11px] text-[#3B82F6] tabular-nums">
                  {r.time}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 7. Assinatura Digital ============ */
function AssinaturaMockup({ active }) {
  return (
    <Frame title="Assinatura Digital" subtitle="Documentos hospitalares">
      <div className="grid grid-cols-5 gap-4">
        {/* Left: document preview */}
        <div className="col-span-3 bg-[#111827] border border-gray-800 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSignature className="w-3.5 h-3.5 text-[#3B82F6]" strokeWidth={1.75} />
              <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
                Termo de consentimento · DOC-4821
              </p>
            </div>
            <span className="font-mono-axis text-[10px] text-[#3B82F6]">tablet Android</span>
          </div>

          {/* Document body */}
          <div className="p-4 space-y-2">
            {[92, 85, 78, 90, 88, 74, 82, 68].map((w, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, width: 0 }}
                animate={active ? { opacity: 1, width: `${w}%` } : { opacity: 0, width: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.5 }}
                className="h-1 bg-gray-700"
              />
            ))}

            {/* Signature area */}
            <div className="mt-6 border border-dashed border-[#3B82F6]/40 p-4 relative overflow-hidden">
              <p className="font-mono-axis text-[9px] uppercase tracking-widest text-gray-500 mb-2">
                Assinatura do responsável
              </p>
              <svg viewBox="0 0 300 50" className="w-full h-10">
                <motion.path
                  d="M 10 35 Q 25 10, 45 30 T 90 25 Q 110 15, 130 30 T 175 28 Q 195 12, 220 32 T 265 25 L 285 30"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={active ? { pathLength: 1 } : { pathLength: 0 }}
                  transition={{ duration: 1.8, delay: 0.6, ease: "easeInOut" }}
                />
              </svg>
              <div className="mt-3 flex items-center gap-2">
                <Pen className="w-3 h-3 text-[#3B82F6]" />
                <p className="text-[10px] text-gray-400 font-mono-axis">
                  Dr. Paulo Safatle · 11/Nov/2026 · 14:32
                </p>
              </div>
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="sweep absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-[#3B82F6]/10 to-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: audit trail */}
        <div className="col-span-2 space-y-3">
          <div className="bg-[#111827] border border-[#3B82F6]/30 p-4">
            <div className="flex items-center gap-2 mb-3">
              <CircleCheck className="w-4 h-4 text-[#3B82F6]" />
              <p className="font-display text-sm font-medium text-white">Assinado</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                <Fingerprint className="w-3 h-3 shrink-0" />
                <span className="font-mono-axis truncate">
                  Hash: <span className="text-[#3B82F6]">a3f9e2b1</span>
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                <ShieldCheck className="w-3 h-3 shrink-0" />
                <span>ICP-Brasil · válido</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                <User className="w-3 h-3 shrink-0" />
                <span>2 signatários</span>
              </div>
            </div>
          </div>

          <div className="bg-[#111827] border border-gray-800 p-4">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500 mb-2">
              Papel economizado
            </p>
            <p className="font-display text-2xl font-semibold text-[#3B82F6] tabular-nums">
              <Counter to={12842} active={active} />
            </p>
            <p className="mt-1 text-[10px] text-gray-500">folhas · este ano</p>
          </div>

          <div className="bg-[#111827] border border-gray-800 p-4">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500 mb-2">
              Documentos assinados
            </p>
            <p className="font-display text-2xl font-semibold text-white tabular-nums">
              <Counter to={4287} active={active} />
            </p>
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 8. Anexos & Documentos ============ */
function AnexosMockup({ active }) {
  const files = [
    { name: "guia-consulta.pdf", pat: "Maria Silva", size: "1.2 MB", type: "pdf" },
    { name: "exame-hemograma.pdf", pat: "João P. Costa", size: "820 KB", type: "pdf" },
    { name: "RX-torax.jpg", pat: "Ana Cardoso", size: "3.4 MB", type: "img" },
    { name: "laudo-cardiologia.pdf", pat: "Carlos M.", size: "540 KB", type: "pdf" },
    { name: "receita-medica.pdf", pat: "Beatriz L.", size: "180 KB", type: "pdf" },
    { name: "TC-abdomen.jpg", pat: "Rafael S.", size: "5.1 MB", type: "img" },
  ];
  return (
    <Frame title="Anexos" subtitle="Gestão de documentos · faturamento">
      <div className="grid grid-cols-5 gap-4">
        {/* Left: Search + stats */}
        <div className="col-span-2 space-y-3">
          <div className="bg-[#111827] border border-gray-800 p-3">
            <div className="flex items-center gap-2 border border-[#3B82F6]/40 bg-[#0B1220] px-3 py-2">
              <Search className="w-3 h-3 text-[#3B82F6]" />
              <span className="text-[11px] text-white font-mono-axis">
                atd:8921 <span className="text-gray-500">|</span> paciente
              </span>
              <motion.span
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 1.2, repeat: Infinity }}
                className="ml-auto w-0.5 h-3 bg-[#3B82F6]"
              />
            </div>
            <p className="font-mono-axis text-[9px] uppercase tracking-widest text-gray-500 mt-3">
              Filtros ativos
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {["convênio · Unimed", "tipo · PDF", "período · Nov/2026"].map((f) => (
                <span
                  key={f}
                  className="text-[9px] px-2 py-1 border border-gray-700 text-gray-400 font-mono-axis"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>

          {[
            { label: "Anexos indexados", value: 8942, c: "#3B82F6", icon: FolderSearch },
            { label: "Tempo médio de busca", value: 2.4, suf: "s", c: "#06B6D4", icon: TrendingUp, d: 1 },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-[#111827] border border-gray-800 p-3">
                <Icon className="w-3.5 h-3.5 mb-2" style={{ color: k.c }} strokeWidth={1.75} />
                <p className="font-display text-lg font-semibold text-white tabular-nums">
                  <Counter to={k.value} decimals={k.d ?? 0} suffix={k.suf || ""} active={active} />
                </p>
                <p className="mt-1 font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
                  {k.label}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right: files grid */}
        <div className="col-span-3 bg-[#111827] border border-gray-800 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
            <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">
              Anexos · atendimento AT-8921
            </p>
            <span className="font-mono-axis text-[10px] text-[#3B82F6]">
              {files.length} arquivos
            </span>
          </div>
          <div className="grid grid-cols-2 gap-px bg-gray-800">
            {files.map((f, i) => (
              <motion.div
                key={f.name}
                initial={{ opacity: 0, y: 10 }}
                animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ delay: 0.06 * i, duration: 0.4 }}
                className="bg-[#111827] p-3 flex items-start gap-2 hover:bg-white/5 transition-colors"
              >
                <div
                  className="w-8 h-10 border flex items-center justify-center shrink-0"
                  style={{
                    borderColor: f.type === "img" ? "#06B6D4" : "#1E3A8A",
                    color: f.type === "img" ? "#06B6D4" : "#1E3A8A",
                  }}
                >
                  <FileText className="w-3.5 h-3.5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-white font-mono-axis truncate">
                    {f.name}
                  </p>
                  <p className="text-[9px] text-gray-500 mt-0.5">
                    {f.pat} · {f.size}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ============ 0. SYNIR ============ */
const SYNIR_CODES = [
  "1012", "2033", "1744", "3021", "0918", "4410", "1193", "2208", "3317", "0451",
  "1880", "2741", "0632", "3399", "1427", "2915", "0786", "4102", "1550", "2604",
  "3118", "0279", "1966", "2457", "3890",
];
const SYNIR_SECTORS = [
  { s: "Laboratório", t: "Laudo sem liberação", st: "Impeditiva", c: "#F87171" },
  { s: "Equipe médica", t: "Evolução feita, não liberada", st: "Aberta", c: "#FBBF24" },
  { s: "Recepção", t: "Guia corrigida (Entrada Única)", st: "Resolvida", c: "#34D399" },
  { s: "Enfermagem", t: "Relato de material", st: "Resolvida", c: "#34D399" },
];

function SynirMockup({ active }) {
  return (
    <Frame title="SYNIR" subtitle="Auditoria & faturamento · P.A.">
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          {[
            { v: 455, l: "Auditadas", c: "#22D3EE" },
            { v: 289, l: "Protocoladas", c: "#34D399" },
            { v: 7, l: "Não faturáv.", c: "#60A5FA" },
            { v: 150, l: "Pendências", c: "#FBBF24" },
          ].map((k) => (
            <div key={k.l} className="bg-[#111827] border border-gray-800 p-3">
              <p className="font-display text-lg lg:text-xl font-semibold tabular-nums" style={{ color: k.c }}>
                <Counter to={k.v} active={active} />
              </p>
              <p className="mt-1 font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">{k.l}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-5 gap-3">
          <div className="col-span-3 bg-[#111827] border border-gray-800 p-4">
            <p className="font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
              Recusa do TASY · Conta 000418
            </p>
            <div className="mt-3 flex flex-wrap gap-1">
              {SYNIR_CODES.map((c, i) => (
                <motion.span
                  key={c}
                  initial={{ opacity: 1 }}
                  animate={active ? { opacity: i === 11 ? 1 : 0.18 } : { opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.03, duration: 0.4 }}
                  className={`font-mono-axis text-[9px] px-1.5 py-0.5 border ${
                    i === 11 ? "border-[#F87171] text-[#F87171]" : "border-gray-700 text-gray-400"
                  }`}
                >
                  {c}
                </motion.span>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
              transition={{ delay: 1.8, duration: 0.5 }}
              className="mt-3 border-l-2 border-[#F87171] pl-3"
            >
              <p className="text-[10px] font-mono-axis uppercase tracking-widest text-[#F87171]">
                25 → 1 · Impede o fechamento
              </p>
              <p className="mt-1 text-[11px] text-white">Data do item fora do atendimento</p>
              <p className="mt-1 text-[10px] text-[#34D399] flex items-center gap-1">
                <CircleCheck className="w-3 h-3" /> Corrigir cateter 24G (14:00) → 11:20–13:31
              </p>
            </motion.div>
          </div>
          <div className="col-span-2 bg-[#111827] border border-gray-800 p-4">
            <p className="font-mono-axis text-[9px] uppercase tracking-widest text-gray-500">
              Pendências por setor
            </p>
            <div className="mt-3 space-y-2.5">
              {SYNIR_SECTORS.map((r, i) => (
                <motion.div
                  key={r.s}
                  initial={{ opacity: 0, x: 10 }}
                  animate={active ? { opacity: 1, x: 0 } : { opacity: 0, x: 10 }}
                  transition={{ delay: 0.4 + i * 0.15 }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-white truncate">{r.s}</span>
                    <span className="font-mono-axis text-[8px] uppercase" style={{ color: r.c }}>
                      {r.st}
                    </span>
                  </div>
                  <p className="text-[9px] text-gray-500 truncate">{r.t}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Frame>
  );
}


/* ============ Carousel ============ */
const SYSTEMS = [
  {
    id: "synir",
    name: "SYNIR",
    tagline: "Motor de auditoria e faturamento do P.A. — integrado ao TASY",
    icon: ScanSearch,
    Component: SynirMockup,
    kpi: "455 contas auditadas e 289 protocoladas em um único dia",
  },
  {
    id: "wely",
    name: "Wely",
    tagline: "BI Hospitalar — indicadores em tempo real",
    icon: BarChart3,
    Component: WelyMockup,
    kpi: "62% mais rápido nas decisões gerenciais",
  },
  {
    id: "huggin",
    name: "HUGGIN",
    tagline: "Auditoria hospitalar centralizada",
    icon: ClipboardCheck,
    Component: HugginMockup,
    kpi: "Redução de 62% no tempo de auditoria",
  },
  {
    id: "muninn",
    name: "MUNINN",
    tagline: "IA para análise de glosas",
    icon: BrainCircuit,
    Component: MuninnMockup,
    kpi: "R$ 487k recuperáveis identificados por mês",
  },
  {
    id: "eirai",
    name: "EIRAI",
    tagline: "Assistente clínico com IA",
    icon: Bot,
    Component: EiraiMockup,
    kpi: "Confiança clínica média de 94.7%",
  },
  {
    id: "escalamed",
    name: "EscalaMed",
    tagline: "Escalas médicas inteligentes",
    icon: CalendarClock,
    Component: EscalaMedMockup,
    kpi: "96% de cobertura automatizada",
  },
  {
    id: "autorizacoes",
    name: "Autorizações",
    tagline: "Automação junto às operadoras",
    icon: ShieldCheck,
    Component: AutorizacoesMockup,
    kpi: "412 horas / mês economizadas",
  },
  {
    id: "assinatura",
    name: "Assinatura",
    tagline: "Assinatura digital hospitalar",
    icon: FileSignature,
    Component: AssinaturaMockup,
    kpi: "12.842 folhas de papel economizadas / ano",
  },
  {
    id: "anexos",
    name: "Anexos",
    tagline: "Gestão de documentos e anexos",
    icon: FolderSearch,
    Component: AnexosMockup,
    kpi: "Busca de anexos em 2.4s (era ~4 min)",
  },
];

const AUTO_MS = 7000;

export default function SystemPreview() {
  const [idx, setIdx] = useState(0);
  const containerRef = useRef(null);
  const inView = useInView(containerRef, { amount: 0.3 });
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!inView || paused) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SYSTEMS.length), AUTO_MS);
    return () => clearTimeout(t);
  }, [idx, inView, paused]);

  const active = SYSTEMS[idx];
  const ActiveMockup = active.Component;
  const ActiveIcon = active.icon;

  const go = (dir) =>
    setIdx((i) => (i + dir + SYSTEMS.length) % SYSTEMS.length);

  return (
    <section
      ref={containerRef}
      data-testid="system-preview-section"
      className="relative bg-white py-24 lg:py-32 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <TechBackground density={45} className="opacity-70" />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#3B82F6]/5 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#1E3A8A]/5 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left copy */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-mono-axis text-xs uppercase tracking-widest text-[#3B82F6] mb-4"
            >
              <ScrambleText text="/02 · Sistemas em ação" />
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05]"
            >
              Uma suíte completa, <br />
              <span className="text-[#1E3A8A]">integrada ao Tasy.</span>
            </motion.h2>
            <p className="mt-6 text-base md:text-lg text-gray-600 leading-relaxed">
              Nossos produtos consomem dados diretamente do{" "}
              <span className="text-[#111827] font-medium">ERP Tasy</span> e
              entregam experiência de uso premium em cada área da operação
              hospitalar.
            </p>

            {/* Active system card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                className="mt-10 border-l-2 border-[#3B82F6] pl-6"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#111827] text-white flex items-center justify-center">
                    <ActiveIcon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500">
                      Produto {String(idx + 1).padStart(2, "0")} / {String(SYSTEMS.length).padStart(2, "0")}
                    </p>
                    <p className="font-display text-2xl font-semibold text-[#111827] tracking-tighter">
                      {active.name}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-600">{active.tagline}</p>
                <p className="mt-3 flex items-center gap-2 font-mono-axis text-[11px] text-[#3B82F6] uppercase tracking-widest">
                  <Sparkles className="w-3 h-3" />
                  {active.kpi}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Nav dots */}
            <div className="mt-10 flex items-center gap-3">
              <button
                type="button"
                onClick={() => go(-1)}
                data-testid="system-prev"
                aria-label="Sistema anterior"
                className="w-9 h-9 border border-gray-300 hover:border-[#3B82F6] hover:text-[#3B82F6] flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                data-testid="system-next"
                aria-label="Próximo sistema"
                className="w-9 h-9 border border-gray-300 hover:border-[#3B82F6] hover:text-[#3B82F6] flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="flex-1 flex items-center gap-2 pl-4">
                {SYSTEMS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setIdx(i)}
                    data-testid={`system-dot-${s.id}`}
                    aria-label={`Ir para ${s.name}`}
                    className="relative h-1 flex-1 bg-gray-200 overflow-hidden"
                  >
                    {i === idx && (
                      <motion.span
                        key={idx + "-progress"}
                        initial={{ width: 0 }}
                        animate={{ width: paused ? "50%" : "100%" }}
                        transition={{ duration: paused ? 0 : AUTO_MS / 1000, ease: "linear" }}
                        className="absolute inset-y-0 left-0 bg-[#3B82F6]"
                      />
                    )}
                    {i < idx && <span className="absolute inset-0 bg-[#3B82F6]/40" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Product tabs */}
            <div className="mt-6 flex flex-wrap gap-2">
              {SYSTEMS.map((s, i) => {
                const Ic = s.icon;
                const isActive = i === idx;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setIdx(i)}
                    data-testid={`system-tab-${s.id}`}
                    className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-[#111827] border-[#111827] text-white"
                        : "border-gray-200 text-gray-600 hover:border-[#3B82F6] hover:text-[#3B82F6]"
                    }`}
                  >
                    <Ic className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mockup */}
          <div className="lg:col-span-7 relative">
            <div
              aria-hidden
              className="absolute -top-5 -right-5 w-full h-full border-2 border-[#3B82F6]/30"
            />
            <div aria-hidden className="absolute -inset-6 bg-[#3B82F6]/10 blur-2xl glow-breathe pointer-events-none" />
            <div aria-hidden className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
              <div className="scanline" />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 20, scale: 0.98, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -20, scale: 0.98, filter: "blur(8px)" }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <ActiveMockup active={inView} />
              </motion.div>
            </AnimatePresence>

            {/* Floating KPI badge */}
            <motion.div
              key={active.id + "-badge"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="hidden md:flex absolute -bottom-6 -left-6 bg-white border border-gray-200 shadow-lg p-4 items-center gap-3"
            >
              <div className="w-9 h-9 bg-[#3B82F6]/10 flex items-center justify-center">
                <ArrowUpRight className="w-4 h-4 text-[#3B82F6]" />
              </div>
              <div>
                <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500">
                  {active.name}
                </p>
                <p className="text-sm font-medium text-[#111827] max-w-[220px] leading-snug">
                  {active.kpi}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
